import {
  Box,
  Button,
  IconButton,
  Input,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Flex,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContext, useState } from "react";
import { apiRequest } from "utils/api";
import MoneyField from "components/elements/MoneyInput";
import { useParams } from "react-router-dom";
import { LoanContext } from "providers/loan";
import { AlertsContext } from "providers/alerts";
import BoxHeader from "components/elements/BoxHeader";
import { IconAlert } from "components/widgets/IconAlert";
import { getErrorMessage } from "utils/errorHandler";
import { useCustomFormats } from "hooks/useCustomFormat";
import moment from "moment";
import { useTenant } from "modules/tenant/providers/tenant-provider";

const BulletPayments = ({ editing, isOpen }: { editing: boolean; isOpen: boolean }) => {
  const { user } = useTenant();
  const { id } = useParams();
  const {
    loan,
    refetch: loadLoan,
    accountsReceivables,
    setAccountsReceivables,
  } = useContext(LoanContext);
  const { showSuccess, showError } = useContext(AlertsContext);
  const [date, setDate] = useState(null);
  const [amount, setAmount] = useState<string | number>("");
  const [status, setStatus] = useState("");
  const [addNewBulletPayment, setAddNewBulletPayment] = useState(false);
  const [description, setDesription] = useState("");
  const [loading, setLoading] = useState(false);

  const { formatDate, formatCurrency } = useCustomFormats();

  const getBulletPayments = async () => {
    try {
      const { data } = await apiRequest("/supabase/accounts_receivable/select", { loan: id });
      setAccountsReceivables(data);
    } catch (error) {
      console.log(error);
    }
  };

  const handleSave = async () => {
    if (amount == null || status == null || date == null) {
      showError({
        title: "Please fill all the fields",
        description: "Please fill all the fields",
      });
      return;
    }

    if (moment(date, "YYYY-MM-DD").isBefore(moment(loan?.funding_date, "YYYY-MM-DD"))) {
      showError({
        title: "Invalid Date",
        description: "Payment date must be greater than Funding date.",
      });
      return;
    }

    try {
      setLoading(true);
      await apiRequest("/supabase/accounts_receivable/insert", {
        paymentAmount: amount,
        paymentStatus: status,
        paymentDate: date,
        description: description,
        loan: id,
      });
      showSuccess({
        title: "Payment Added",
        description: "The payment has been added",
      });
    } catch (error) {
      console.error(error);
    } finally {
      await loadLoan();
      setDate(null);
      setAmount("");
      setStatus("");
      setDesription("");
      setLoading(false);
      setAddNewBulletPayment(false);
      getBulletPayments();
    }
  };

  const handleAddPayment = () => {
    const funding_date = loan?.funding_date ?? null;
    if (!funding_date) {
      showError({
        title: "Funding date not set",
        description: "Please set the funding date",
      });
    } else {
      setAddNewBulletPayment(true);
    }
  };

  const handleDelete = async (id: any) => {
    try {
      setLoading(true);
      await apiRequest("/supabase/accounts_receivable/delete", {
        id: id,
      });
      showSuccess({
        title: "Payment Deleted",
        description: "The payment has been deleted",
      });
    } catch (error) {
      showError({
        title: "Something went wrong",
        description: getErrorMessage(error),
      });
    } finally {
      setLoading(false);
      getBulletPayments();
    }
  };

  const handleCancel = () => {
    setDate(null);
    setAmount("");
    setStatus("");
    setAddNewBulletPayment(false);
  };

  return (
    <Flex direction="column" gap={4}>
      {/* <BoxHeader mb="10px" title="Bullet Payments" /> */}
      <Flex alignItems="center" justifyContent="space-between">
        {<BoxHeader mb="0px" title={"Bullet Payments"} />}
        {!isOpen && <IconAlert mb="0px" text="Bullet Payments are not visible to borrowers." />}
        {user.type === "lender" && (
          <Button onClick={handleAddPayment} variant="white" size="sm">
            Add Bullet Payment
          </Button>
        )}
      </Flex>

      {isOpen && <IconAlert text="Bullet Payments are not visible to borrowers." />}
      {addNewBulletPayment && (
        <Box display="flex" justifyContent="space-between" width="100%" mt="5" alignItems="center">
          <Box width="20%">
            <Text color={"black"} mb="2">
              Description
            </Text>
            <Input
              flex="1"
              value={description}
              placeholder="Enter description..."
              onChange={event => setDesription(event.target.value)}
              type="text"
              variant="primary"
              minHeight="2.5rem"
            />
          </Box>
          <Box width="20%" ml={"-30px"}>
            <Text color={"black"} mb="2">
              Payment Date
            </Text>
            <Input
              flex="1"
              value={date ? date : ""}
              onChange={(event: any) => setDate(event.target.value)}
              type="date"
              variant="primary"
              minHeight="2.5rem"
            />
          </Box>
          <Box ml={"-25px"}>
            <Text color={"black"} mb="2">
              Payment Amount
            </Text>
            <Box w="60%">
              <MoneyField value={amount} onValueChange={val => setAmount(val ?? "")} />
            </Box>
          </Box>
          <Box ml="-13%">
            <Text color={"black"} mb="2">
              Payment Status
            </Text>
            <Box>
              <Select
                placeholder="Select option"
                value={status}
                onChange={e => setStatus(e.target.value)}
              >
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
                <option value="approved">Approved</option>
                <option value="declined">Declined</option>
              </Select>
            </Box>
          </Box>
          <Box alignSelf="flex-end" mt={"-20px"}>
            <IconButton
              mt={"-20px"}
              width="20px"
              // height="0px"
              size="20px"
              mr="8px"
              isLoading={loading}
              background="transparent"
              aria-label="add-new-item"
              borderWidth="0px"
              fontSize="20px"
              color="primary"
              onClick={() => handleSave()}
              _hover={{
                background: "transparent",
                color: "#E2DFE8",
              }}
              icon={<FontAwesomeIcon icon={["far", "check"]} />}
            />
            <IconButton
              mt={"-20px"}
              width="20px"
              // height="20px"
              size="20px"
              ml={"5px"}
              mr="8px"
              // isLoading={loading}
              background="transparent"
              aria-label="add-new-item"
              borderWidth="0px"
              fontSize="20px"
              color="primary"
              onClick={() => handleCancel()}
              _hover={{
                background: "transparent",
                color: "#E2DFE8",
              }}
              icon={<FontAwesomeIcon icon={["far", "remove"]} />}
            />
          </Box>
        </Box>
      )}
      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>Payment Number</Th>
              <Th>Description</Th>
              <Th>Payment Date</Th>
              <Th ml="50px">Payment Amount</Th>
              <Th>Payment Status</Th>
              {editing && <Th></Th>}
            </Tr>
          </Thead>
          <Tbody>
            {accountsReceivables?.length > 0 ? (
              accountsReceivables
                .sort(
                  (a: any, b: any) =>
                    moment(b.payment_date, "YYYY-MM-DD").valueOf() -
                    moment(a.payment_date, "YYYY-MM-DD").valueOf()
                )
                .map((bulletPayment: any, index: any) => (
                  <Tr key={bulletPayment.id} borderBottom="none">
                    <Td>{bulletPayment.id || ""}</Td>
                    <Td>{bulletPayment.description || ""}</Td>
                    <Td>{formatDate(bulletPayment?.payment_date)}</Td>
                    <Td>{formatCurrency(bulletPayment.payment_amount || 0)}</Td>
                    <Td>{bulletPayment?.payment_status || ""}</Td>
                    {editing && (
                      <Td>
                        <IconButton
                          variant="outline"
                          aria-label="delete-field"
                          onClick={() => handleDelete(bulletPayment.id)}
                          icon={<FontAwesomeIcon icon={["far", "trash"]} />}
                        />
                      </Td>
                    )}
                  </Tr>
                ))
            ) : (
              <Tr>
                <Td colSpan={editing ? 6 : 5}>
                  <Text textAlign="center" p={4}>
                    No bullet payments yet.
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Flex>
  );
};

export default BulletPayments;
